import { z } from 'zod'
import { localDateSchema } from './attendance.ts'

/**
 * Service attendance: the Sunday count, and what it says over time.
 *
 * The numbers here get read from a pulpit and quoted in a members' meeting, so
 * the arithmetic is written once, tested against fixtures worked out by hand,
 * and shared by the API and both clients rather than recomputed per screen.
 */

/**
 * A starting point, not a schema. Churches split their count differently and
 * change the split when a ministry starts, so the category is free text in the
 * database; this is what the UI offers before anyone has typed their own.
 */
export const SUGGESTED_HEADCOUNT_CATEGORIES = [
  'Adults',
  'Children',
  'Youth',
  'Volunteers',
  'Online',
] as const

export interface SessionCount {
  /** Local date, YYYY-MM-DD. */
  sessionDate: string
  campusId: string | null
  name: string
  counts: Record<string, number>
}

/** Total across every category of one session. */
export const sessionTotal = (session: SessionCount): number =>
  Object.values(session.counts).reduce((sum, count) => sum + count, 0)

export interface WeekPoint {
  /** The Sunday (or configured week start) that this bucket begins on. */
  weekStart: string
  total: number
  /** Per-category totals, so a chart can stack them. */
  byCategory: Record<string, number>
  sessions: number
}

/**
 * Weekly totals, bucketed by the week each service fell in.
 *
 * Weeks start on Sunday because that is the day being counted; a Monday-start
 * week would split a Sunday-evening service from its own morning.
 */
export function weeklyTotals(sessions: readonly SessionCount[]): WeekPoint[] {
  const buckets = new Map<string, WeekPoint>()

  for (const session of sessions) {
    const weekStart = startOfWeek(session.sessionDate)
    const bucket = buckets.get(weekStart) ?? {
      weekStart,
      total: 0,
      byCategory: {},
      sessions: 0,
    }
    bucket.sessions += 1
    for (const [category, count] of Object.entries(session.counts)) {
      bucket.total += count
      bucket.byCategory[category] = (bucket.byCategory[category] ?? 0) + count
    }
    buckets.set(weekStart, bucket)
  }

  return [...buckets.values()].sort((a, b) => a.weekStart.localeCompare(b.weekStart))
}

/** The Sunday on or before a date, as YYYY-MM-DD. */
export function startOfWeek(date: string): string {
  const parsed = new Date(`${date}T00:00:00Z`)
  if (Number.isNaN(parsed.getTime())) return date
  parsed.setUTCDate(parsed.getUTCDate() - parsed.getUTCDay())
  return parsed.toISOString().slice(0, 10)
}

export interface Trend {
  weeks: WeekPoint[]
  /** Mean weekly total across weeks that had at least one service. */
  averageWeekly: number
  /**
   * Recent average against the one before it, as a percentage change.
   *
   * Four weeks against the previous four by default: shorter and Christmas
   * looks like growth, longer and a real decline takes a quarter to show up.
   * null when there is not enough history to compare honestly.
   */
  changePercent: number | null
  /** Highest and lowest weeks, which is what people ask about next. */
  best: WeekPoint | null
  worst: WeekPoint | null
}

export function summarizeTrend(sessions: readonly SessionCount[], window = 4): Trend {
  const weeks = weeklyTotals(sessions)
  if (weeks.length === 0) {
    return { weeks, averageWeekly: 0, changePercent: null, best: null, worst: null }
  }

  const totals = weeks.map((week) => week.total)
  const averageWeekly = round1(totals.reduce((sum, total) => sum + total, 0) / weeks.length)

  // Both halves have to be complete, or the "change" is really "we have less
  // data than you think".
  const recent = weeks.slice(-window)
  const previous = weeks.slice(-window * 2, -window)
  const changePercent =
    recent.length === window && previous.length === window
      ? percentChange(mean(previous.map((w) => w.total)), mean(recent.map((w) => w.total)))
      : null

  const sorted = [...weeks].sort((a, b) => a.total - b.total)
  return {
    weeks,
    averageWeekly,
    changePercent,
    best: sorted.at(-1) ?? null,
    worst: sorted[0] ?? null,
  }
}

const mean = (values: number[]) =>
  values.length === 0 ? 0 : values.reduce((sum, value) => sum + value, 0) / values.length

/** null when there is nothing to grow from — "up ∞%" helps nobody. */
function percentChange(before: number, after: number): number | null {
  if (before === 0) return null
  return round1(((after - before) / before) * 100)
}

const round1 = (value: number) => Math.round(value * 10) / 10

// ---- schemas ---------------------------------------------------------------

export const sessionCountsSchema = z
  .record(z.string().trim().min(1).max(40), z.number().int().min(0).max(1_000_000))
  .refine((counts) => Object.keys(counts).length <= 20, 'That is a lot of categories')

export const createSessionSchema = z.object({
  name: z.string().trim().min(1, 'Name the service').max(80),
  sessionDate: localDateSchema,
  campusId: z.uuid().nullish(),
  startsAt: z.iso.datetime().nullish(),
  note: z.string().trim().max(500).nullish(),
  /** The check-in service it counts, so its check-ins show beside the count. */
  checkinTimeId: z.uuid().nullish(),
  counts: sessionCountsSchema.default({}),
})
export type CreateSessionInput = z.infer<typeof createSessionSchema>
export type CreateSessionFormInput = z.input<typeof createSessionSchema>

export const updateSessionSchema = createSessionSchema.partial()
export type UpdateSessionInput = z.infer<typeof updateSessionSchema>

// ---- check-ins beside a headcount ---------------------------------------------

export interface SessionKey {
  id: string
  sessionDate: string
  campusId: string | null
  checkinTimeId: string | null
}

/** Check-ins on one local day, at one service time (or none), at one campus. */
export interface CheckinTotal {
  day: string
  timeId: string | null
  campusId: string | null
  total: number
}

/**
 * How many were checked in to each session — shown beside its headcount, the
 * way Planning Center's totals include check-ins.
 *
 * A session tied to a check-in time gets that service's check-ins that day.
 * One that is not gets the day's check-ins at its campus (every campus, for a
 * church-wide session) only when it is the *only* session that day there —
 * with two services and no times, there is no honest way to split them, and
 * a figure counted twice is worse than none. Null means "not known".
 */
export function checkedInBySession(
  sessions: readonly SessionKey[],
  totals: readonly CheckinTotal[],
): Map<string, number | null> {
  const result = new Map<string, number | null>()
  const sameDay = (a: SessionKey, b: SessionKey) =>
    a.sessionDate === b.sessionDate &&
    (a.campusId === null || b.campusId === null || a.campusId === b.campusId)

  for (const session of sessions) {
    const day = totals.filter((row) => row.day === session.sessionDate)
    if (session.checkinTimeId) {
      result.set(
        session.id,
        day
          .filter((row) => row.timeId === session.checkinTimeId)
          .reduce((sum, row) => sum + row.total, 0),
      )
      continue
    }
    const alone = sessions.filter((other) => sameDay(session, other)).length === 1
    if (!alone) {
      result.set(session.id, null)
      continue
    }
    const here = day.filter(
      (row) =>
        session.campusId === null || row.campusId === null || row.campusId === session.campusId,
    )
    result.set(session.id, here.length === 0 ? null : here.reduce((sum, row) => sum + row.total, 0))
  }
  return result
}
